/**
 * Shared presentational components for the ArticleDocumentEditor.
 */

import React from 'react';

export function SectionHeading({ id, title, children }: { id: string; title: string; children?: React.ReactNode }) {
  return (
    <div id={id} className="scroll-mt-4">
      <details open>
        <summary className="text-sm font-semibold text-gray-900 cursor-pointer hover:text-gray-700 py-2">
          {title}
        </summary>
        <div className="pl-2 pb-4 space-y-3">
          {children}
        </div>
      </details>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500">{label}</span>
      {children}
    </label>
  );
}

export function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

export function TextArea({ value, onChange, rows, placeholder }: { value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <textarea
      className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm"
      rows={rows || 3}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

export function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs text-gray-400">{label}</span>
      <div className="text-sm text-gray-600 bg-gray-50 rounded px-2 py-1 mt-0.5">
        {value || '\u2014'}
      </div>
    </div>
  );
}

export function PencilIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
      <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.05 10.476a2.5 2.5 0 0 0-.657 1.143l-.656 2.625a.25.25 0 0 0 .304.304l2.625-.656a2.5 2.5 0 0 0 1.143-.657l7.963-7.963a1.75 1.75 0 0 0 0-2.475l-.284-.284Z" />
    </svg>
  );
}

export function TrashIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
      <path fillRule="evenodd" d="M5 3.25V4H2.75a.75.75 0 0 0 0 1.5h.31l.57 7.41A2.25 2.25 0 0 0 5.875 15h4.25a2.25 2.25 0 0 0 2.245-2.09l.57-7.41h.31a.75.75 0 0 0 0-1.5H11v-.75A2.25 2.25 0 0 0 8.75 1h-1.5A2.25 2.25 0 0 0 5 3.25Zm2.25-.75a.75.75 0 0 0-.75.75V4h3v-.75a.75.75 0 0 0-.75-.75h-1.5ZM6.05 6a.75.75 0 0 1 .787.713l.275 5.5a.75.75 0 0 1-1.498.075l-.275-5.5A.75.75 0 0 1 6.05 6Zm3.9 0a.75.75 0 0 1 .712.787l-.275 5.5a.75.75 0 0 1-1.498-.075l.275-5.5A.75.75 0 0 1 9.95 6Z" clipRule="evenodd" />
    </svg>
  );
}

export function StringListEditor({ items, onChange, placeholder }: { items: string[]; onChange: (items: string[]) => void; placeholder?: string }) {
  return (
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
            value={item}
            placeholder={placeholder}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <button
            type="button"
            className="px-1.5 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            x
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={() => onChange([...items, ''])}
      >
        + Add
      </button>
    </div>
  );
}
